import { z } from 'zod';

import { PageQuerySchema } from '../shared/pagination';
import {
  DraftReviewScoresSchema,
  KbEditProposalSchema,
  PromptRuleProposalSchema,
} from './cs-review-proposals';
import { ProductRegistrationDocumentCheckSchema } from './product-registration';

// A structured result an agent records about its own run, kind-discriminated so one
// generic store serves every agent. Kinds are closed on purpose: adding one is always a
// code change (a payload schema plus the thin, scope-pinned tool that writes it), which
// is what keeps a generic jsonb store from ever holding an unvalidated shape.
export const SESSION_OUTCOME_KINDS = [
  'root-cause-verdict',
  'reply-draft',
  'cs-draft',
  'channel-reply',
  'job-result',
  'product-registration-document-check',
  'kb-edit-proposal',
  'prompt-rule-proposal',
  'draft-review',
  'cs-decision',
  'draft-claims',
] as const;
export const SessionOutcomeKindSchema = z.enum(SESSION_OUTCOME_KINDS);
export type SessionOutcomeKind = z.infer<typeof SessionOutcomeKindSchema>;

export const ROOT_CAUSE_VERDICTS = [
  'bug_confirmed',
  'working_as_designed',
  'inconclusive',
] as const;
export const RootCauseVerdictKindSchema = z.enum(ROOT_CAUSE_VERDICTS);

export const VERDICT_CONFIDENCES = ['high', 'medium', 'low'] as const;
export const VerdictConfidenceSchema = z.enum(VERDICT_CONFIDENCES);

// A recurrence key for one failure class, e.g. `billing/anchor-reset-on-plan-change`.
// Slug-shaped so it groups exactly on repeat hits — free text would never match itself.
export const FingerprintSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:[-/][a-z0-9]+)*$/)
  .max(120);

// The root-cause investigator's conclusion. Also the `record-verdict` tool's argument
// schema, so this is what the model is asked to produce.
//
// `rootCauseSummary` and `suggestedCsAction` are the ONLY fields the gateway egress maps
// into the Intercom internal note — they must stay plain-language and CS-safe. Code paths,
// queries and stack traces belong in `technicalDetail`, which never leaves the platform.
export const RootCauseVerdictSchema = z.object({
  // Null when the investigation ran as a sub-run inside a drafting turn: the drafting agent
  // is never told the Intercom conversation id — the gateway owns the thread↔session mapping
  // and reads its conclusions back by (session, turn) — so there is nothing truthful for the
  // model to copy, and requiring it would only invite an invented one. The relay path still
  // carries it, because SBC hands it over in the envelope. Either way the gateway routes on
  // its OWN conversation id, never on this.
  intercomConversationId: z.string().min(1).max(64).nullable().default(null),
  // Null when no Pass A draft is in play — e.g. a run started by hand from the admin UI.
  draftId: z.string().max(64).nullable().default(null),
  verdict: RootCauseVerdictKindSchema,
  confidence: VerdictConfidenceSchema,
  rootCauseSummary: z.string().min(1).max(2000),
  technicalDetail: z.string().max(20_000).default(''),
  affectedCountEstimate: z.string().max(200).default('unknown'),
  fingerprint: FingerprintSchema,
  // Set once the code tools land (they stamp their workspace's commit); null until then.
  repoCommitSha: z.string().max(64).nullable().default(null),
  // Filed or referenced-existing once the Linear tool lands; null until then.
  linearTicket: z.string().max(64).nullable().default(null),
  suggestedCsAction: z.string().min(1).max(2000),
});
export type RootCauseVerdict = z.infer<typeof RootCauseVerdictSchema>;

export const REPLY_DRAFT_MAX_LENGTH = 1000;
export const REPLY_DRAFT_ACTION_MAX_LENGTH = 400;

export const ReplyDraftSchema = z.object({
  inboxConversationId: z.uuid(),
  replyText: z.string().min(1).max(REPLY_DRAFT_MAX_LENGTH),
  confidence: VerdictConfidenceSchema,
  rationale: z.string().max(2000).default(''),
  actionRequired: z.string().max(REPLY_DRAFT_ACTION_MAX_LENGTH).nullable().default(null),
});
export type ReplyDraft = z.infer<typeof ReplyDraftSchema>;

// The CS reply categories the Intercom playbook routes on. Closed because each one names a
// section of that playbook: a category the prompt does not define would leave CS with a
// label nothing explains.
export const CS_DRAFT_CATEGORIES = [
  'order-status',
  'billing-charge',
  'cancel',
  'skip-pause',
  'address-change',
  'product-box',
  'account-login',
  'promo-referral',
  'no-reply-needed',
  'other',
] as const;
export const CsDraftCategorySchema = z.enum(CS_DRAFT_CATEGORIES);
export type CsDraftCategory = z.infer<typeof CsDraftCategorySchema>;

// One support-thread reply the CS draft agent proposes for the turn it just answered, and
// the `record-draft` tool's argument schema. It carries no thread or customer id: the
// gateway owns the thread→session mapping and reads this back by (session, turn), so the
// model is never asked for an identifier it could get wrong or be talked into changing.
//
// `body` is the message as CS will send it, whole. It is deliberately not an array of
// chat bubbles: that shape came from WhatsApp, where a reply really is several messages,
// and an Intercom support thread is email.
//
// `reasoning` and `actionRequired` never reach the member. They are the two things the CS
// agent reading the note acts on — why the draft says what it says, and what a human must
// do before sending it — so they are separate fields rather than a prefix convention the
// egress would have to parse back out of prose.
export const CS_DRAFT_BODY_MAX_LENGTH = 4000;
export const CS_DRAFT_REASONING_MAX_LENGTH = 1000;
export const CS_DRAFT_ACTION_MAX_LENGTH = 400;
export const CS_DRAFT_MACRO_MAX_LENGTH = 200;

export const CsDraftSchema = z.object({
  category: CsDraftCategorySchema,
  body: z.string().min(1).max(CS_DRAFT_BODY_MAX_LENGTH),
  confidence: VerdictConfidenceSchema,
  // True only for an actual instruction-injection attempt in the customer's text. Anger,
  // urgency and threats are not injection — the playbook says so, and this flag drives a
  // CS warning, so a false positive teaches CS to ignore it.
  injectionRisk: z.boolean(),
  reasoning: z.string().min(1).max(CS_DRAFT_REASONING_MAX_LENGTH),
  // Null whenever the draft can be sent as-is. Set only when a human must act first —
  // cancel the subscription, stop an order — because it renders as the note's loudest line
  // and a warning that fires on every draft is a warning CS stops reading.
  actionRequired: z.string().max(CS_DRAFT_ACTION_MAX_LENGTH).nullable().default(null),
  macro: z.string().max(CS_DRAFT_MACRO_MAX_LENGTH).nullable().default(null),
  memberUpset: z.boolean().default(false),
  moneyInvolved: z.boolean().default(false),
});
export type CsDraft = z.infer<typeof CsDraftSchema>;

export const CsDraftArgsSchema = CsDraftSchema.extend({
  memberUpset: z.boolean(),
  moneyInvolved: z.boolean(),
});
export type CsDraftArgs = z.infer<typeof CsDraftArgsSchema>;

// What a deterministic SOP decision tool concluded for one support turn: the action the SOP
// allows, the money and dates it settles on, and whether a human has to decide instead. The
// tool records it itself (rule 10), so the drafter only writes the reply from it and the
// gateway checks the reply's claims against it before anything is sent.
//
// `needsHuman` means the reply itself must wait for a person. `csTask` means the reply can go
// and a person then runs the action, such as a refund in Stripe.
export const CS_DECISION_ACTIONS = [
  'none',
  'cancel-subscription',
  'refund',
  'cancel-and-refund',
] as const;
export const CsDecisionActionSchema = z.enum(CS_DECISION_ACTIONS);
export type CsDecisionAction = z.infer<typeof CsDecisionActionSchema>;

export const SubIntentSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
  .max(80);

export const CsDecisionRefundSchema = z.object({
  amountCents: z.number().int().min(0),
  invoiceId: z.string().min(1).max(64).nullable(),
});

export const CsDecisionBoxSchema = z.object({
  orderNumber: z.string().min(1).max(64).nullable(),
  shipsOn: z.iso.date(),
});

export const CsDecisionSchema = z
  .object({
    topic: CsDraftCategorySchema,
    subIntent: SubIntentSchema,
    sopRule: z.string().min(1).max(200),
    action: CsDecisionActionSchema,
    refund: CsDecisionRefundSchema.nullable(),
    remainingBoxes: z.array(CsDecisionBoxSchema).max(24),
    nextChargeOn: z.iso.date().nullable(),
    csTask: z.string().min(1).max(CS_DRAFT_ACTION_MAX_LENGTH).nullable(),
    needsHuman: z.boolean(),
    needsHumanReason: z.string().min(1).max(500).nullable(),
  })
  .refine((decision) => decision.needsHuman === (decision.needsHumanReason !== null), {
    message: 'needsHumanReason is set exactly when needsHuman is true',
    path: ['needsHumanReason'],
  })
  .refine((decision) => decisionRefunds(decision.action) === (decision.refund !== null), {
    message: 'refund is set exactly when the action refunds',
    path: ['refund'],
  });
export type CsDecision = z.infer<typeof CsDecisionSchema>;

export function decisionRefunds(action: CsDecisionAction): boolean {
  return action === 'refund' || action === 'cancel-and-refund';
}

export function decisionCancels(action: CsDecisionAction): boolean {
  return action === 'cancel-subscription' || action === 'cancel-and-refund';
}

// What the draft verifier read the draft as promising, recorded by its `record-draft-claims`
// tool without seeing the Decision, so the gateway compares the two in code. `draftBody` is
// the text it checked: claims about an earlier version of the draft count for nothing.
export const DRAFT_CLAIM_SUBJECTS = [
  'refund',
  'cancel',
  'remaining-box',
  'next-charge',
  'cs-task',
] as const;
export const DraftClaimSubjectSchema = z.enum(DRAFT_CLAIM_SUBJECTS);
export type DraftClaimSubject = z.infer<typeof DraftClaimSubjectSchema>;

export const DraftClaimsSchema = z.object({
  draftBody: z.string().min(1).max(CS_DRAFT_BODY_MAX_LENGTH),
  refund: z.object({
    promised: z.boolean(),
    amountCents: z.number().int().min(0).nullable(),
  }),
  cancelsSubscription: z.boolean(),
  dates: z.array(z.object({ date: z.iso.date(), about: z.string().min(1).max(200) })).max(20),
  boxes: z
    .array(z.object({ shipsOn: z.iso.date().nullable(), about: z.string().min(1).max(200) }))
    .max(24),
  promises: z
    .array(
      z.object({
        text: z.string().min(1).max(300),
        covers: DraftClaimSubjectSchema.nullable(),
      }),
    )
    .max(20),
});
export type DraftClaims = z.infer<typeof DraftClaimsSchema>;

// One channel-facing reply the agent concludes a turn with, and the `record-reply` tool's
// argument schema. The gateway sends this text — never the run's last-message prose — so a
// turn that records nothing has concluded nothing and nothing goes out.
//
// The cap is the outer bound the store accepts, not a channel limit: each egress enforces its own
// (Instagram cuts a DM at 1,000 characters and a comment at 2,200; Slack falls back to a thread
// reply when an appended translation would push a person's message past 4,000). It is sized for a
// translator turning a long Korean post into English, which runs well past twice its length.
export const CHANNEL_REPLY_MAX_LENGTH = 12000;

export const ChannelReplySchema = z.object({
  text: z.string().min(1).max(CHANNEL_REPLY_MAX_LENGTH),
});
export type ChannelReply = z.infer<typeof ChannelReplySchema>;

// The structured answer to a job SBC handed the platform, and the `record-job-result` tool's
// argument schema. `result` is deliberately opaque: what a job answers with is agreed between
// the agent's prompt and SBC's receiver, so pinning a shape here would mean a platform deploy
// for every new job type — the same exemption the retired relay made for its inbound case file.
// The platform only guarantees it is a JSON object, which is what makes it safe to store in
// jsonb and forward verbatim.
export const JobResultSchema = z.object({
  result: z.record(z.string(), z.unknown()),
});
export type JobResult = z.infer<typeof JobResultSchema>;

const SessionOutcomeBaseSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  // The external entity the outcome is about, e.g. `intercom:215475…`. Derived inside the
  // writing tool from the payload — never a tool argument.
  subjectKey: z.string().nullable(),
  groupKey: z.string().nullable(),
  createdAt: z.iso.datetime(),
});

export const SessionOutcomeSchema = z.discriminatedUnion('kind', [
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('root-cause-verdict'),
    payload: RootCauseVerdictSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('reply-draft'),
    payload: ReplyDraftSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('cs-draft'),
    payload: CsDraftSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('channel-reply'),
    payload: ChannelReplySchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('job-result'),
    payload: JobResultSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('product-registration-document-check'),
    payload: ProductRegistrationDocumentCheckSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('kb-edit-proposal'),
    payload: KbEditProposalSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('prompt-rule-proposal'),
    payload: PromptRuleProposalSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('draft-review'),
    payload: DraftReviewScoresSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('cs-decision'),
    payload: CsDecisionSchema,
  }),
  SessionOutcomeBaseSchema.extend({
    kind: z.literal('draft-claims'),
    payload: DraftClaimsSchema,
  }),
]);
export type SessionOutcome = z.infer<typeof SessionOutcomeSchema>;

// Kinds a TOOL records about work it did itself, rather than a fact the model relayed
// (CLAUDE.md rule 10). The gateway forwards every one the turn recorded to the receiver
// alongside the run's `job-result`, so the receiver applies the whole run in one transaction.
// The list lives here rather than in the gateway because the gateway is generic by
// construction — nothing in it knows what any particular job is about.
export const TOOL_AUTHORED_OUTCOME_KINDS = ['product-registration-document-check'] as const;
export type ToolAuthoredOutcomeKind = (typeof TOOL_AUTHORED_OUTCOME_KINDS)[number];

const TOOL_AUTHORED_OUTCOME_PARSERS: Record<
  ToolAuthoredOutcomeKind,
  (payload: unknown) => Record<string, unknown>
> = {
  'product-registration-document-check': (payload) =>
    ProductRegistrationDocumentCheckSchema.parse(payload),
};

// The wire shape the gateway posts: the stored payload, re-parsed because it has been through
// jsonb, tagged with the kind the receiver discriminates on. The tag is added here rather than
// stored on the row, which already carries the kind in its own column.
export function toolOutcomeWireShape(
  kind: ToolAuthoredOutcomeKind,
  payload: unknown,
): Record<string, unknown> {
  return { kind, ...TOOL_AUTHORED_OUTCOME_PARSERS[kind](payload) };
}

// --- Read models for the admin UI ---

// Every kind is projected onto one row shape, so the admin list holds no per-kind branch.
// `Record<SessionOutcomeKind, …>` is the point: a sixth kind does not compile until it says
// how it renders and whether it is held — which is the same "adding a kind is a code change"
// bargain SESSION_OUTCOME_KINDS already makes.
export const OutcomeRowProjectionSchema = z.object({
  // The kind's own headline classification — a verdict, a CS category. Null for kinds that
  // classify nothing, which is what keeps the column generic rather than verdict-shaped.
  label: z.string().nullable(),
  confidence: VerdictConfidenceSchema.nullable(),
  summary: z.string(),
  // The one outward reference the kind carries, e.g. a filed Linear ticket.
  externalRef: z.string().nullable(),
  // True when a human must look before this outcome is acted on. Each kind's rule is the one
  // already shipped elsewhere — the investigator's hold path, the ⚠️ lines the Intercom note
  // renders — restated here so one queue can span every agent.
  needsReview: z.boolean(),
});
export type OutcomeRowProjection = z.infer<typeof OutcomeRowProjectionSchema>;

// Each kind pairs its own payload schema with its projector. Binding the two together is what
// lets the registry be exhaustive without a single type assertion: a kind can never be read
// through another kind's shape.
function projector<S extends z.ZodType>(
  schema: S,
  project: (payload: z.infer<S>) => OutcomeRowProjection,
): (payload: unknown) => OutcomeRowProjection | null {
  return (payload) => {
    const parsed = schema.safeParse(payload);
    return parsed.success ? project(parsed.data) : null;
  };
}

const OUTCOME_PROJECTORS: Record<
  SessionOutcomeKind,
  (payload: unknown) => OutcomeRowProjection | null
> = {
  'root-cause-verdict': projector(RootCauseVerdictSchema, (payload) => ({
    label: payload.verdict,
    confidence: payload.confidence,
    summary: payload.rootCauseSummary,
    externalRef: payload.linearTicket,
    // The TRD §5 hold path: a confirmed bug the model is unsure of files no ticket and waits.
    needsReview: payload.verdict === 'bug_confirmed' && payload.confidence === 'low',
  })),
  'reply-draft': projector(ReplyDraftSchema, (payload) => ({
    label: null,
    confidence: payload.confidence,
    summary: payload.replyText,
    externalRef: null,
    needsReview: payload.confidence === 'low' || payload.actionRequired !== null,
  })),
  'cs-draft': projector(CsDraftSchema, (payload) => ({
    label: payload.category,
    confidence: payload.confidence,
    summary: payload.body,
    externalRef: null,
    // The three things cs-draft-note.ts already marks with an alert for the CS agent reading
    // the note, so the queue holds exactly what that note already shouts about.
    needsReview:
      payload.confidence === 'low' || payload.injectionRisk || payload.actionRequired !== null,
  })),
  // Already sent when it was recorded, so nothing here is holdable.
  'channel-reply': projector(ChannelReplySchema, (payload) => ({
    label: null,
    confidence: null,
    summary: payload.text,
    externalRef: null,
    needsReview: false,
  })),
  // `result` is opaque by contract — its shape is agreed between the agent's prompt and SBC's
  // receiver — so the row can only show which keys came back, never judge them.
  'job-result': projector(JobResultSchema, (payload) => ({
    label: null,
    confidence: null,
    summary: Object.keys(payload.result).join(', '),
    externalRef: null,
    needsReview: false,
  })),
  // SBC re-checks the verdict against the current file and skips a stale one, so nothing here
  // is held for a human: `fail` and `missing` are answers the brand is emailed about, not
  // errors. `registration_id` is the one outward reference the kind carries.
  'product-registration-document-check': projector(
    ProductRegistrationDocumentCheckSchema,
    (payload) => ({
      label: payload.verdict,
      confidence: null,
      summary: payload.reason,
      externalRef: payload.registration_id,
      needsReview: false,
    }),
  ),
  'kb-edit-proposal': projector(KbEditProposalSchema, (payload) => ({
    label: payload.articleId === null ? 'new-article' : 'article-edit',
    confidence: null,
    summary: payload.rationale,
    externalRef: payload.articlePath,
    needsReview: true,
  })),
  'prompt-rule-proposal': projector(PromptRuleProposalSchema, (payload) => ({
    label: payload.agentSlug,
    confidence: null,
    summary: payload.rule,
    externalRef: null,
    needsReview: true,
  })),
  'draft-review': projector(DraftReviewScoresSchema, (payload) => ({
    label: `${payload.accuracy}/${payload.style}/${payload.judgement}/${payload.match}`,
    confidence: null,
    summary: payload.note ?? '',
    externalRef: null,
    needsReview: false,
  })),
  'cs-decision': projector(CsDecisionSchema, (payload) => ({
    label: `${payload.topic} · ${payload.subIntent}`,
    confidence: null,
    summary: payload.needsHumanReason ?? `${payload.action} (${payload.sopRule})`,
    externalRef: null,
    needsReview: payload.needsHuman,
  })),
  'draft-claims': projector(DraftClaimsSchema, (payload) => ({
    label: null,
    confidence: null,
    summary: payload.promises.map((promise) => promise.text).join(' · '),
    externalRef: null,
    needsReview: false,
  })),
};

// The payload has been through jsonb, so it is parsed rather than cast. A row written before
// its schema changed projects to null instead of failing the whole page.
export function projectOutcome(
  kind: SessionOutcomeKind,
  payload: unknown,
): OutcomeRowProjection | null {
  return OUTCOME_PROJECTORS[kind](payload);
}

// One row of the admin outcomes list: the store's own generic columns, the resolved session
// and agent, and the kind's projection. `payload` is deliberately absent — a CS draft body or
// a case file can carry member data, and a list does not need it.
export const SessionOutcomeSummarySchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  sessionName: z.string().nullable(),
  agentName: z.string(),
  agentSlug: z.string(),
  kind: SessionOutcomeKindSchema,
  messageSeq: z.number().int(),
  subjectKey: z.string().nullable(),
  groupKey: z.string().nullable(),
  // How many outcomes share this row's group key. Null when the row has none. Counted for
  // every kind, not just verdicts, so a kind that starts grouping needs no view change.
  repeatCount: z.number().int().positive().nullable(),
  // Null when the stored payload no longer parses against its kind's schema.
  projection: OutcomeRowProjectionSchema.nullable(),
  createdAt: z.iso.datetime(),
});
export type SessionOutcomeSummary = z.infer<typeof SessionOutcomeSummarySchema>;

export const ListSessionOutcomesQuerySchema = PageQuerySchema.extend({
  kind: SessionOutcomeKindSchema.optional(),
  confidence: VerdictConfidenceSchema.optional(),
  groupKey: z.string().min(1).optional(),
  sessionId: z.uuid().optional(),
  // The review queue: every kind's own hold rule, unioned. Coerced because it binds off a
  // query string.
  needsReview: z.stringbool().optional(),
});
export type ListSessionOutcomesQuery = z.infer<typeof ListSessionOutcomesQuerySchema>;

export const SessionOutcomePageSchema = z.object({
  items: z.array(SessionOutcomeSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type SessionOutcomePage = z.infer<typeof SessionOutcomePageSchema>;
